import { Gauge, TrendUp, TrendDown, Minus } from '@phosphor-icons/react';
import type { ColumnDef } from '../components/common/DataTable';
import { TruncatedText } from '../components/common/TruncatedText';
import { lookupOr } from '../lib/lookupOr';
import type { RiskLevel, ScoreBand, TrendDirection } from '@discovr/types';
import { EMPTY_VALUE, LOW_CONFIDENCE_TEXT, NO_COMMENTS_YET, formatSentimentScore } from './reportFormat';
import { round1, roundInt } from './sentimentReportView';
import type { SentimentCreatorRow } from './sentimentReportView';

// Sentiment-score pill (Figma "scorepill"): green tint for healthy scores,
// amber for the middle band, red for the low band. The backend picks the band.
const SCORE_PILL_CLASS: Record<ScoreBand, string> = {
  positive: 'bg-emerald-50 text-emerald-700',
  mixed: 'bg-amber-50 text-amber-700',
  negative: 'bg-red-50 text-red-700',
};

// Risk gauge colour (Figma "Campaign Risk Gauge"); the backend picks the level.
const RISK_COLOR: Record<RiskLevel, string> = { HIGH: '#DC2626', MEDIUM: '#E08A1E', LOW: '#15803D' };
const UNKNOWN_RISK_COLOR = 'var(--muted-foreground)';
const UNKNOWN_SCORE_PILL_CLASS = 'bg-muted text-muted-foreground';

// Per-row 7-day trend (Figma "trend" cell): TrendUp/TrendDown glyph + delta pts.
// The backend decides the direction; a creator with no earlier 7-day window to
// compare against has a null trend and an empty direction, which shows a dash
// (never "stable", which would read as a measured 0-pt change).
const RowTrendBadge = ({ trend, direction }: { trend: number | null; direction: TrendDirection }) => {
  if (trend === null || (direction !== 'up' && direction !== 'down' && direction !== 'stable'))
    return <span className="text-[14px] font-medium text-muted-foreground">{EMPTY_VALUE}</span>;
  const points = round1(trend);
  if (direction === 'up')
    return (
      <span className="flex items-center gap-1 text-[14px] font-medium text-emerald-700">
        <TrendUp size={16} /> +{points}pts
      </span>
    );
  if (direction === 'down')
    return (
      <span className="flex items-center gap-1 text-[14px] font-medium text-red-600">
        <TrendDown size={16} /> {points}pts
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-[14px] font-medium text-muted-foreground">
      <Minus size={16} /> stable
    </span>
  );
};

const NoValue = () => <span className="text-[14px] font-medium text-muted-foreground">{EMPTY_VALUE}</span>;

// The creator's score since registration; "No comments yet" until they have any.
const TrackingScoreCell = ({ row }: { row: SentimentCreatorRow }) => {
  if (row.tracking === null) return <NoValue />;
  if (row.commentsSinceRegistration === 0)
    return <span className="block whitespace-normal text-[11px] leading-tight text-muted-foreground">{NO_COMMENTS_YET}</span>;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span
        title={row.trackingLowConfidence ? LOW_CONFIDENCE_TEXT : undefined}
        className={`inline-flex h-8 min-w-[50px] items-center justify-center rounded-lg px-2.5 text-[14px] font-medium ${lookupOr(SCORE_PILL_CLASS, row.trackingBand, UNKNOWN_SCORE_PILL_CLASS)} ${row.trackingLowConfidence ? 'opacity-60' : ''}`}
      >
        {formatSentimentScore(row.tracking)}
      </span>
    </div>
  );
};

// ── Per-Creator Sentiment DataTable columns ────────────────────────────────────

export const perCreatorColumns: ColumnDef<SentimentCreatorRow>[] = [
  {
    id: 'creator',
    header: 'Creator',
    sticky: 'left',
    // Fixed, not grow: every column here is a single line of text/pill of
    // similar weight (unlike Active Campaigns' Campaign column, which also
    // carries an icon + subtitle and genuinely needs more room). With no
    // grow column, DataTable's useDistributedGap spreads leftover width
    // evenly across every column's padding instead of dumping it all into
    // one — the balanced fit for a table shaped like this one. A plain
    // truncating text cell (no avatar), so a much lower floor than the
    // avatar+handle Creator cell used in roster tables is fine here.
    minWidth: 200,
    cell: (c) => (
      <TruncatedText
        text={c.creator_name}
        className="block text-[14px] font-medium text-foreground"
      />
    ),
  },
  {
    id: 'sentiment_score',
    header: 'Sentiment',
    minWidth: 84,
    cell: (c) => <TrackingScoreCell row={c} />,
  },
  {
    id: 'positive_pct',
    header: 'Positive %',
    minWidth: 145,
    cell: (c) => {
      if (!c.hasWindowScore) return <NoValue />;
      const pos = roundInt(c.pos_pct);
      return (
        <div className="flex items-center gap-3">
          <div className="h-1.5 w-[80px] overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full"
              style={{ width: `${pos}%`, backgroundColor: '#15803D' }}
            />
          </div>
          <span className="text-[14px] font-medium text-foreground">{pos}%</span>
        </div>
      );
    },
  },
  {
    id: 'risk_index',
    header: 'Risk Index',
    minWidth: 130,
    cell: (c) => {
      if (!c.hasWindowScore) return <NoValue />;
      const ri = round1(c.risk_index);
      return (
        <div className="flex items-center gap-2">
          <Gauge size={20} weight="duotone" style={{ color: lookupOr(RISK_COLOR, c.risk_level, UNKNOWN_RISK_COLOR) }} />
          <div className="leading-tight">
            <div className="text-[14px] font-medium text-foreground">{ri}</div>
            <div className="text-[10px] uppercase text-muted-foreground">{c.risk_level}</div>
          </div>
        </div>
      );
    },
  },
  {
    id: 'comments',
    header: 'Comments',
    minWidth: 84,
    align: 'right',
    cell: (c) => (
      <span className="text-[14px] text-foreground">
        {c.comments_analyzed.toLocaleString()}
      </span>
    ),
  },
  {
    id: 'trend_7d',
    header: 'Trend 7D',
    minWidth: 110,
    cell: (c) =>
      c.hasWindowScore ? <RowTrendBadge trend={c.trend_7d} direction={c.trend_direction} /> : <NoValue />,
  },
];

